import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { DocumentViewer } from '../components/DocumentViewer';
import {
  FolderOpen, Upload, Search, Trash2, Eye, Download,
  File, FileText, Image, ChevronRight, Printer, Filter
} from 'lucide-react';

// ── Configuración de secciones con subsecciones ───────────────────────
const SECCIONES_CONFIG = [
  { value: 'pnts', label: 'PNTs y Procedimientos', tieneSubseccion: true, subseccionLabel: 'PNT específico' },
  { value: 'proveedores', label: 'Proveedores', tieneSubseccion: true, subseccionLabel: 'Proveedor' },
  { value: 'contratos', label: 'Contratos', tieneSubseccion: false },
  { value: 'limpieza', label: 'Limpieza', tieneSubseccion: false },
  { value: 'temperatura', label: 'Control Temperatura', tieneSubseccion: false },
  { value: 'plagas', label: 'Control de Plagas', tieneSubseccion: false },
  { value: 'devoluciones', label: 'Devoluciones', tieneSubseccion: false },
  { value: 'no-conformidades', label: 'No Conformidades', tieneSubseccion: false },
  { value: 'formacion', label: 'Formación', tieneSubseccion: false },
  { value: 'general', label: 'General', tieneSubseccion: false },
];

function FileIcon({ mime, nombre, size = 18 }) {
  const ext = (nombre || '').split('.').pop().toLowerCase();
  if ((mime || '').includes('pdf') || ext === 'pdf') return <FileText size={size} color="#ef4444" />;
  if ((mime || '').includes('image') || ['jpg','jpeg','png','gif','bmp'].includes(ext)) return <Image size={size} color="#3b82f6" />;
  if (['doc','docx'].includes(ext)) return <FileText size={size} color="#2563eb" />;
  if (['xls','xlsx','csv'].includes(ext)) return <FileText size={size} color="#16a34a" />;
  return <File size={size} color="#6366f1" />;
}

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Panel de subida con selector en cascada ───────────────────────────
function PanelSubida({ onSubido }) {
  const [seccion, setSeccion] = useState('general');
  const [subseccionId, setSubseccionId] = useState('');
  const [subseccionNombre, setSubseccionNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [cargandoOpciones, setCargandoOpciones] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const seccionConfig = SECCIONES_CONFIG.find(s => s.value === seccion);

  useEffect(() => {
    setSubseccionId('');
    setSubseccionNombre('');
    if (!seccionConfig?.tieneSubseccion) return;

    setCargandoOpciones(true);
    if (seccion === 'pnts') {
      // Cargar todos los PNTs agrupados por categoría
      api.get('/pnts', { params: { limit: 100 } })
        .then(r => setOpciones(r.data.pnts || []))
        .finally(() => setCargandoOpciones(false));
    } else if (seccion === 'proveedores') {
      api.get('/proveedores')
        .then(r => setOpciones(r.data || []))
        .finally(() => setCargandoOpciones(false));
    }
  }, [seccion]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('seccion', seccion);
    formData.append('descripcion', descripcion || '');
    if (subseccionId) {
      formData.append('entidad_tipo', seccion === 'pnts' ? 'pnt' : 'proveedor');
      formData.append('entidad_id', subseccionId);
      formData.append('subseccion', subseccionNombre);
    }
    setUploading(true);
    try {
      await api.post('/archivos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSubido();
      setDescripcion('');
      setSubseccionId('');
      setSubseccionNombre('');
      e.target.value = '';
    } catch (err) {
      alert(err.response?.data?.error || 'Error al subir archivo');
    } finally { setUploading(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Upload size={16} color="var(--color-primary)" /> Subir Nuevo Documento
        </h3>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Paso 1: Sección */}
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ background: 'var(--color-primary)', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>1</span>
              Sección
            </label>
            <select className="form-control" style={{ width: 210 }} value={seccion} onChange={e => setSeccion(e.target.value)}>
              {SECCIONES_CONFIG.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Paso 2: PNT o Proveedor (solo si aplica) */}
          {seccionConfig?.tieneSubseccion && (
            <>
              <div style={{ color: 'var(--color-text-3)', alignSelf: 'flex-end', paddingBottom: 10 }}>
                <ChevronRight size={16} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ background: 'var(--color-primary)', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>2</span>
                  {seccionConfig.subseccionLabel}
                </label>
                {cargandoOpciones ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1.5px solid var(--color-gray-3)', borderRadius: 6 }}>
                    <div className="spinner" style={{ width: 14, height: 14 }} />
                    <span style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>Cargando...</span>
                  </div>
                ) : (
                  <select className="form-control" style={{ width: 280 }} value={subseccionId}
                    onChange={e => {
                      const opt = opciones.find(o => String(o.id) === e.target.value);
                      setSubseccionId(e.target.value);
                      setSubseccionNombre(opt ? (seccion === 'pnts' ? `${opt.codigo} - ${opt.titulo}` : opt.nombre) : '');
                    }}>
                    <option value="">— Seleccionar {seccionConfig.subseccionLabel} —</option>
                    {seccion === 'pnts' ? (
                      opciones.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.codigo} — {p.titulo}
                        </option>
                      ))
                    ) : (
                      opciones.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)
                    )}
                  </select>
                )}
              </div>
            </>
          )}

          {/* Paso 3: Descripción */}
          <div style={{ color: 'var(--color-text-3)', alignSelf: 'flex-end', paddingBottom: 10 }}>
            <ChevronRight size={16} />
          </div>
          <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 180 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ background: 'var(--color-gray-3)', color: 'var(--color-text-2)', borderRadius: '50%', width: 18, height: 18, fontSize: '0.7rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                {seccionConfig?.tieneSubseccion ? '3' : '2'}
              </span>
              Descripción (opcional)
            </label>
            <input className="form-control" placeholder="Ej: Registro firmado, Contrato 2026..."
              value={descripcion} onChange={e => setDescripcion(e.target.value)} />
          </div>

          {/* Botón subir */}
          <label className="btn btn-primary" style={{ cursor: uploading ? 'wait' : 'pointer', marginBottom: 0, alignSelf: 'flex-end' }}>
            {uploading
              ? <><div className="spinner" style={{ width: 16, height: 16 }} /> Subiendo...</>
              : <><Upload size={16} /> Seleccionar Archivo</>
            }
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={handleUpload}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.bmp" disabled={uploading} />
          </label>
        </div>

        {/* Indicador de destino */}
        {(seccion || subseccionNombre) && (
          <div style={{ marginTop: 10, fontSize: '0.78rem', color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FolderOpen size={13} />
            Destino:
            <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
              {SECCIONES_CONFIG.find(s => s.value === seccion)?.label}
              {subseccionNombre && <> <ChevronRight size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {subseccionNombre}</>}
            </span>
          </div>
        )}

        <p style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 8, marginBottom: 0 }}>
          Formatos: PDF, Word (.doc/.docx), Excel (.xls/.xlsx), JPG, PNG · Máx. 50 MB
        </p>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────
export default function ArchivosPage() {
  const { isAdmin } = useAuth();
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [visor, setVisor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/archivos', { params: { q, seccion: filtroSeccion } })
      .then(r => setArchivos(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [q, filtroSeccion]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este archivo? Esta acción no se puede deshacer.')) return;
    await api.delete(`/archivos/${id}`);
    load();
  };

  // Agrupar archivos por sección y subsección
  const agrupados = archivos.reduce((acc, a) => {
    const sec = a.seccion || 'general';
    const sub = a.subseccion || '';
    const key = sub ? `${sec}|||${sub}` : sec;
    if (!acc[key]) acc[key] = { seccion: sec, subseccion: sub, archivos: [] };
    acc[key].archivos.push(a);
    return acc;
  }, {});

  const grupos = Object.values(agrupados).sort((a, b) => {
    if (a.seccion !== b.seccion) return a.seccion.localeCompare(b.seccion);
    return (a.subseccion || '').localeCompare(b.subseccion || '');
  });

  const seccionLabel = (s) => SECCIONES_CONFIG.find(x => x.value === s)?.label || s;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FolderOpen size={24} color="var(--color-primary)" /> Documentos Subidos
        </h1>
        <p>{archivos.length} archivos en el sistema · Haz clic en el ojo 👁 para previsualizar cualquier documento</p>
      </div>

      {/* Panel de subida */}
      <PanelSubida onSubido={load} />

      {/* Filtros */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="search-wrap">
            <Search size={15} />
            <input className="search-input" placeholder="Buscar por nombre o descripción..."
              value={q} onChange={e => setQ(e.target.value)} style={{ width: 280 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Filter size={14} color="var(--color-text-3)" />
            <select className="form-control" style={{ width: 220 }} value={filtroSeccion} onChange={e => setFiltroSeccion(e.target.value)}>
              <option value="">Todas las secciones</option>
              {SECCIONES_CONFIG.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {(q || filtroSeccion) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setQ(''); setFiltroSeccion(''); }}>✕ Limpiar</button>
          )}
        </div>
      </div>

      {/* Lista agrupada */}
      {loading ? (
        <div className="loading-overlay"><div className="spinner" /><span>Cargando documentos...</span></div>
      ) : archivos.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FolderOpen size={48} />
            <h3>Sin documentos</h3>
            <p>Sube el primer documento usando el panel de arriba</p>
          </div>
        </div>
      ) : (
        // Vista agrupada cuando no hay búsqueda activa
        q || filtroSeccion ? (
          // Vista plana con búsqueda
          <div className="card">
            <div style={{ padding: '10px 16px', background: 'var(--color-gray-1)', borderBottom: '1px solid var(--color-gray-3)', fontSize: '0.8rem', color: 'var(--color-text-3)' }}>
              {archivos.length} resultado(s) encontrado(s)
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th style={{ width: 36 }}></th><th>Nombre</th><th>Sección</th><th>Descripción</th><th>Subido por</th><th>Tamaño</th><th>Fecha</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                  {archivos.map(a => (
                    <FilaArchivo key={a.id} a={a} onVer={() => setVisor(a)} onDelete={handleDelete} isAdmin={isAdmin()} seccionLabel={seccionLabel} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Vista agrupada por sección y subsección
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {grupos.map((grupo, gi) => (
              <GrupoArchivos
                key={gi}
                grupo={grupo}
                seccionLabel={seccionLabel}
                onVer={setVisor}
                onDelete={handleDelete}
                isAdmin={isAdmin()}
              />
            ))}
          </div>
        )
      )}

      {/* Visor de documentos */}
      {visor && <DocumentViewer archivo={visor} onClose={() => setVisor(null)} />}
    </div>
  );
}

// ── Grupo de archivos por sección/subsección ──────────────────────────
function GrupoArchivos({ grupo, seccionLabel, onVer, onDelete, isAdmin }) {
  const [expandido, setExpandido] = useState(true);
  const SECCION_COLORS = {
    pnts: '#e91e8c', proveedores: '#9c27b0', contratos: '#2196f3',
    limpieza: '#4caf50', temperatura: '#ef4444', plagas: '#795548',
    devoluciones: '#ff5722', 'no-conformidades': '#f59e0b',
    formacion: '#3f51b5', general: '#607d8b'
  };
  const color = SECCION_COLORS[grupo.seccion] || '#607d8b';

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {/* Cabecera del grupo */}
      <div
        onClick={() => setExpandido(!expandido)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 20px', cursor: 'pointer',
          background: `${color}10`,
          borderBottom: expandido ? `1px solid ${color}30` : 'none',
          userSelect: 'none'
        }}
      >
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color }}>
            {seccionLabel(grupo.seccion)}
          </div>
          {grupo.subseccion && (
            <div style={{ fontSize: '0.78rem', color: 'var(--color-text-3)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ChevronRight size={11} /> {grupo.subseccion}
            </div>
          )}
        </div>
        <span style={{ fontSize: '0.75rem', background: `${color}20`, color, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>
          {grupo.archivos.length} archivo{grupo.archivos.length !== 1 ? 's' : ''}
        </span>
        <span style={{ color: 'var(--color-text-3)', fontSize: '0.9rem' }}>{expandido ? '▲' : '▼'}</span>
      </div>

      {/* Tabla de archivos */}
      {expandido && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                <th>Nombre del archivo</th>
                <th>Descripción</th>
                <th>Subido por</th>
                <th>Tamaño</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {grupo.archivos.map(a => (
                <FilaArchivo key={a.id} a={a} onVer={() => onVer(a)} onDelete={onDelete} isAdmin={isAdmin} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Fila individual de archivo ────────────────────────────────────────
function FilaArchivo({ a, onVer, onDelete, isAdmin, seccionLabel }) {
  return (
    <tr>
      <td><FileIcon mime={a.tipo_mime} nombre={a.nombre_original} /></td>
      <td style={{ fontWeight: 500, maxWidth: 260 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nombre_original}</div>
      </td>
      {seccionLabel && <td><span style={{ fontSize: '0.75rem', background: 'var(--color-gray-2)', color: 'var(--color-text-2)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>{seccionLabel(a.seccion)}</span></td>}
      <td style={{ fontSize: '0.82rem', color: 'var(--color-text-2)' }}>{a.descripcion || a.subseccion || '—'}</td>
      <td style={{ fontSize: '0.82rem' }}>{a.subido_por_nombre || '—'}</td>
      <td style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>{formatSize(a.tamaño)}</td>
      <td style={{ fontSize: '0.78rem', color: 'var(--color-text-3)' }}>{new Date(a.created_at).toLocaleDateString('es-ES')}</td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="btn btn-ghost btn-icon btn-sm" title="Previsualizar / Imprimir" onClick={onVer}
            style={{ color: 'var(--color-primary)' }}>
            <Eye size={14} />
          </button>
          <a href={`${window.location.port==='3000'?'http://localhost:3001':''}/api/archivos/${a.id}/descargar?t=${localStorage.getItem('token')||''}`}
            className="btn btn-ghost btn-icon btn-sm" title="Descargar" target="_blank" rel="noreferrer">
            <Download size={14} />
          </a>
          {isAdmin && (
            <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--color-danger)' }}
              onClick={() => onDelete(a.id)} title="Eliminar">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
